import React from 'react';
import SuffixSelector from '../../components/SuffixSelector';
import { driveAccessFailureHint } from '../../utils/api';
import { WEB_SEARCH_PROVIDERS, isFeelLuckyEnabled } from '../../utils/webSearchProviders';
import {
    RESEARCH_TIMEOUT_DEFAULT_MINUTES,
    RESEARCH_TIMEOUT_MAX_MINUTES,
    RESEARCH_TIMEOUT_MIN_MINUTES,
} from '../../utils/researchDefaults';
import {
    CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT,
    CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MAX,
    CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MIN,
} from '../../utils/chatToolResultDefaults';
import { SettingsMcpPanel } from './SettingsMcpPanel';
import { ActiveUserSelectionsPanel } from './ActiveUserSelectionsPanel';

export function SettingsUserTab({ p }) {
    return (
                <div className="settings-tab-content">
                    {p.userSettingsLoading && (
                        <div className="loading">
                            <i className="fas fa-spinner fa-spin"></i> Loading user settings...
                        </div>
                    )}

                    <ActiveUserSelectionsPanel p={p} />

                    {/* Active Horizon Selection */}
                    <div className="settings-category" id="suffix-settings">
                        <h3>
                            <i className="fas fa-database"></i> Active Horizon
                            <span className="badge badge-user">USER</span>
                        </h3>
                        <SuffixSelector />
                    </div>

                    {/* Dark Mode Toggle */}
                    <div className="settings-category" id="appearance-settings">
                        <h3>
                            <i className="fas fa-palette"></i> Appearance
                            <span className="badge badge-user">USER</span>
                        </h3>
                        <div className="setting-item">
                            <div>
                                <div className="setting-label">Theme</div>
                                <div className="setting-description">
                                    Choose between light and dark mode for the interface
                                </div>
                            </div>
                            <div className="setting-value">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={p.toggleDarkMode}
                                    disabled={!p.user || !p.user.email}
                                    title={p.darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                                >
                                    <i className={`fas ${p.darkMode ? 'fa-sun' : 'fa-moon'}`}></i>
                                    {p.darkMode ? 'Light Mode' : 'Dark Mode'}
                                </button>
                            </div>
                        </div>
                        <div className="setting-item">
                            <div>
                                <div className="setting-label">Project chat Markdown</div>
                                <div className="setting-description">
                                    Render assistant replies with headings, lists, and code formatting in project chats
                                </div>
                            </div>
                            <div className="setting-value">
                                <label className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={p.chatMarkdownEnabled !== false}
                                        onChange={() => p.toggleChatMarkdown()}
                                        disabled={!p.user || !p.user.email}
                                    />
                                    {p.chatMarkdownEnabled !== false ? 'On' : 'Off'}
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Chat tool results */}
                    <div className="settings-category" id="chat-tool-result-settings">
                        <h3>
                            <i className="fas fa-database"></i> Chat tool results
                            <span className="badge badge-user">USER</span>
                        </h3>
                        <div className="setting-item">
                            <div>
                                <div className="setting-label">Max characters per field</div>
                                <div className="setting-description">
                                    How much of each long tool-result field (e.g. ABM reports) is returned per call
                                    before text paging (<code>text_page</code> / <code>text_limit</code>).
                                    Default {CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT.toLocaleString()}.
                                    Your value is used when the model omits <code>text_limit</code> — raise up to{' '}
                                    {CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MAX.toLocaleString()} for fuller text in fewer trips,
                                    or lower to save context tokens.
                                </div>
                            </div>
                            <div className="setting-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="number"
                                    min={CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MIN}
                                    max={CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MAX}
                                    step={1000}
                                    className="form-control"
                                    style={{ width: 120 }}
                                    value={
                                        p.userSettings.chatToolResultMaxFieldLength
                                        ?? CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT
                                    }
                                    onChange={(e) => {
                                        const n = Number(e.target.value);
                                        p.handleUserSettingsChange(
                                            'chatToolResultMaxFieldLength',
                                            Number.isFinite(n) ? n : CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT
                                        );
                                    }}
                                    disabled={p.userSettingsLoading || !p.user || !p.user.email}
                                    aria-label="Max characters per chat tool result field"
                                />
                                <span className="setting-description">
                                    chars ({CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MIN.toLocaleString()}–
                                    {CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MAX.toLocaleString()})
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Deep Research */}
                    <div className="settings-category" id="deep-research-settings">
                        <h3>
                            <i className="fas fa-flask"></i> Deep Research
                            <span className="badge badge-user">USER</span>
                        </h3>
                        <div className="setting-item">
                            <div>
                                <div className="setting-label">Research timeout</div>
                                <div className="setting-description">
                                    How long a Deep Research job may run before it is aborted (default {RESEARCH_TIMEOUT_DEFAULT_MINUTES} minutes).
                                    Longer timeouts help large briefs on slower models like gpt-5.
                                </div>
                            </div>
                            <div className="setting-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="number"
                                    min={RESEARCH_TIMEOUT_MIN_MINUTES}
                                    max={RESEARCH_TIMEOUT_MAX_MINUTES}
                                    step={1}
                                    className="form-control"
                                    style={{ width: 96 }}
                                    value={p.userSettings.researchTimeoutMinutes ?? RESEARCH_TIMEOUT_DEFAULT_MINUTES}
                                    onChange={(e) => {
                                        const n = Number(e.target.value);
                                        p.handleUserSettingsChange(
                                            'researchTimeoutMinutes',
                                            Number.isFinite(n) ? n : RESEARCH_TIMEOUT_DEFAULT_MINUTES
                                        );
                                    }}
                                    disabled={p.userSettingsLoading || !p.user || !p.user.email}
                                    aria-label="Deep Research timeout in minutes"
                                />
                                <span className="setting-description">
                                    minutes ({RESEARCH_TIMEOUT_MIN_MINUTES}–{RESEARCH_TIMEOUT_MAX_MINUTES})
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Per-User Google OAuth App Credentials */}
                    <div className="settings-category" id="user-oauth-credentials">
                        <h3>
                            <i className="fas fa-key"></i> Google OAuth App Credentials
                            <span className="badge badge-user">USER</span>
                        </h3>
                        <p className="section-description" style={{marginBottom: '15px', color: '#9ca3af', fontSize: '0.9em'}}>
                            Each organization connects their own Google Cloud project. To set up:
                        </p>
                        <ol className="section-description" style={{marginBottom: '15px', color: '#9ca3af', fontSize: '0.85em', paddingLeft: '20px', lineHeight: '1.8'}}>
                            <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" style={{color: '#60a5fa'}}>Google Cloud Console → Credentials</a></li>
                            <li>Create an <strong>OAuth 2.0 Client ID</strong> (Web application)</li>
                            <li>Enable the <strong>Google Drive API</strong> and <strong>Google Sheets API</strong> in your project</li>
                            <li>Add <code style={{fontSize: '0.9em', wordBreak: 'break-all'}}>{`${p.runtimeInfo.apiUri}/casc/v1/api/google/auth/callback`}</code> as an <strong>Authorized Redirect URI</strong></li>
                            <li>Paste the OAuth client identifier and redirect URI below and save. The client secret may stay on the server only (<code>GOOGLE_CLIENT_SECRET</code> / <code>GOOGLE_OAUTH_CLIENT_SECRET</code>) — leave that field blank in that case.</li>
                        </ol>
                        {p.userGoogleCredentials.loading ? (
                            <div className="loading"><i className="fas fa-spinner fa-spin"></i> Loading credentials...</div>
                        ) : (
                            <>
                                <div className="setting-item">
                                    <div>
                                        <div className="setting-label">Import shared Google credentials</div>
                                        <div className="setting-description">
                                            Import public admin-managed OAuth credentials available to your email domain or every email address.
                                        </div>
                                    </div>
                                    <div className="setting-value">
                                        {p.userGoogleCredentialImportsLoading ? (
                                            <div className="loading" style={{padding: '8px 0'}}>
                                                <i className="fas fa-spinner fa-spin"></i> Loading imports...
                                            </div>
                                        ) : p.userGoogleCredentialImports.length > 0 ? (
                                            <div style={{display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap'}}>
                                                <select
                                                    value={p.selectedGoogleCredentialImportId}
                                                    onChange={e => p.setSelectedGoogleCredentialImportId(e.target.value)}
                                                    disabled={p.userGoogleCredentialImporting || p.userGoogleCredentialsSaving}
                                                    style={{minWidth: '260px'}}
                                                >
                                                    {p.userGoogleCredentialImports.map(provider => (
                                                        <option key={provider.id} value={provider.id}>
                                                            {provider.name}
                                                            {provider.email_scope === 'all' ? ' (all domains)' : ` (${provider.domain})`}
                                                        </option>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={p.handleImportUserGoogleCredentials}
                                                    disabled={p.userGoogleCredentialImporting || p.userGoogleCredentialsSaving || !p.selectedGoogleCredentialImportId}
                                                >
                                                    {p.userGoogleCredentialImporting
                                                        ? <><i className="fas fa-spinner fa-spin"></i> Importing...</>
                                                        : <><i className="fas fa-file-import"></i> Import</>}
                                                </button>
                                            </div>
                                        ) : (
                                            <span style={{fontSize: '0.9em', color: '#9ca3af'}}>
                                                No public shared Google credentials are available for your email domain.
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="setting-item">
                                    <div>
                                        <div className="setting-label">OAuth client identifier</div>
                                        <div className="setting-description">
                                            The <strong>Client ID</strong> string from Google Cloud Console → APIs &amp; Credentials (OAuth 2.0 Web client). This is an app identifier, not a username.
                                        </div>
                                    </div>
                                    <div className="setting-value">
                                        <input
                                            type="text"
                                            id="cascade-oauth2-web-client-id"
                                            name="cascade-oauth2-web-client-id"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck={false}
                                            data-1p-ignore
                                            data-lpignore="true"
                                            data-form-type="other"
                                            value={p.userGoogleCredentials.client_id}
                                            onChange={e => p.setUserGoogleCredentials(prev => ({...prev, client_id: e.target.value}))}
                                            placeholder="e.g. 123456789-….apps.googleusercontent.com"
                                            disabled={p.userGoogleCredentialsSaving}
                                        />
                                    </div>
                                </div>
                                <div className="setting-item">
                                    <div>
                                        <div className="setting-label">OAuth client secret</div>
                                        <div className="setting-description">
                                            The <strong>Client secret</strong> for that same OAuth client (app credential — not your Zagger account password). Optional if it is only set on the API server; leave blank to keep a stored secret.
                                        </div>
                                    </div>
                                    <div className="setting-value">
                                        <input
                                            type="text"
                                            id="cascade-oauth2-web-client-secret"
                                            name="cascade-oauth2-web-client-secret"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck={false}
                                            data-1p-ignore
                                            data-lpignore="true"
                                            data-form-type="other"
                                            value={p.userGoogleCredentials.client_secret}
                                            onChange={e => p.setUserGoogleCredentials(prev => ({...prev, client_secret: e.target.value}))}
                                            placeholder={p.userGoogleCredentials.hasCredentials ? 'Leave blank to keep current secret; paste only to replace' : 'Optional — or use GOOGLE_CLIENT_SECRET on the server'}
                                            disabled={p.userGoogleCredentialsSaving}
                                        />
                                    </div>
                                </div>
                                <div className="setting-item">
                                    <div>
                                        <div className="setting-label">OAuth redirect URI</div>
                                        <div className="setting-description">
                                            Add this exact URI to your Google Cloud Console&apos;s Authorized Redirect URIs:<br/>
                                            <code style={{fontSize: '0.9em', wordBreak: 'break-all'}}>{`${p.runtimeInfo.apiUri}/casc/v1/api/google/auth/callback`}</code>
                                        </div>
                                    </div>
                                    <div className="setting-value">
                                        <input
                                            type="url"
                                            id="cascade-oauth2-redirect-uri"
                                            name="cascade-oauth2-redirect-uri"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            spellCheck={false}
                                            data-1p-ignore
                                            data-lpignore="true"
                                            data-form-type="other"
                                            value={p.userGoogleCredentials.redirect_uri || `${p.runtimeInfo.apiUri}/casc/v1/api/google/auth/callback`}
                                            onChange={e => p.setUserGoogleCredentials(prev => ({...prev, redirect_uri: e.target.value}))}
                                            placeholder={`${p.runtimeInfo.apiUri}/casc/v1/api/google/auth/callback`}
                                            disabled={p.userGoogleCredentialsSaving}
                                        />
                                    </div>
                                </div>
                                <div style={{marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px'}}>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={p.handleSaveUserGoogleCredentials}
                                        disabled={p.userGoogleCredentialsSaving || !p.userGoogleCredentials.client_id}
                                    >
                                        {p.userGoogleCredentialsSaving
                                            ? <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                                            : <><i className="fas fa-save"></i> Save OAuth Credentials</>}
                                    </button>
                                    {p.userGoogleCredentials.hasCredentials && (
                                        <span style={{fontSize: '0.85em', color: '#10b981'}}>
                                            <i className="fas fa-check-circle"></i> Credentials saved
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Personal Google Drive Connection */}
                    <div className="settings-category" id="personal-google-settings">
                        <h3>
                            <i className="fab fa-google-drive"></i> Personal Google Drive
                            <span className="badge badge-user">USER</span>
                        </h3>
                        <p className="section-description" style={{marginBottom: '15px', color: '#9ca3af', fontSize: '0.9em'}}>
                            Connect your own Google account to access Google Drive, Sheets, and Docs for ingestion and integrations.
                        </p>
                        <div className="oauth-actions" style={{paddingTop: '0'}}>
                            <div style={{display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
                                {!p.personalGoogleAuth.isConnected && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={p.handleConnectPersonalGoogle}
                                        disabled={p.saving || p.personalGoogleAuth.loading}
                                    >
                                        <i className="fas fa-plug"></i> Connect Personal Google Account
                                    </button>
                                )}
                                {p.personalGoogleAuth.isConnected && (
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={p.handleDisconnectPersonalGoogle}
                                        disabled={p.saving || p.personalGoogleAuth.loading}
                                        style={{
                                            backgroundColor: '#dc2626',
                                            color: '#ffffff',
                                            borderColor: '#dc2626'
                                        }}
                                    >
                                        <i className="fas fa-sign-out-alt"></i> Disconnect Personal Google Account
                                    </button>
                                )}
                            </div>
                            <div className="oauth-status-info" style={{marginTop: '10px', fontSize: '0.9em', color: '#666'}}>
                                {p.personalGoogleAuth.loading ? (
                                    <span><i className="fas fa-spinner fa-spin"></i> Checking personal Google status...</span>
                                ) : p.personalGoogleAuth.isConnected && p.personalGoogleAuth.canAccessDrive ? (
                                    <span style={{color: '#10b981'}}>
                                        <i className="fas fa-check-circle"></i> Personal Google account connected
                                        {p.personalGoogleAuth.activeSource === 'user' && (
                                            <span style={{marginLeft: '6px', fontWeight: 500}}>(active)</span>
                                        )}
                                    </span>
                                ) : p.personalGoogleAuth.isConnected ? (
                                    <div>
                                        <span style={{color: '#f59e0b'}}>
                                            <i className="fas fa-exclamation-triangle"></i> Signed in, but Google Drive is not reachable
                                        </span>
                                        <div style={{ marginTop: '8px', fontSize: '0.88em', color: '#9ca3af', lineHeight: 1.4 }}>
                                            {driveAccessFailureHint(p.personalGoogleAuth.driveAccessFailureReason)}
                                        </div>
                                    </div>
                                ) : (
                                    <span style={{color: '#6b7280'}}>
                                        <i className="fas fa-info-circle"></i> No personal Google account connected
                                    </span>
                                )}
                                {p.personalGoogleAuth.connectedUser && (
                                    <div style={{ marginTop: '6px', fontSize: '0.92em', color: '#888' }}>
                                        <i className="fas fa-user" style={{ marginRight: '5px' }}></i>
                                        Account: <strong style={{ color: '#d4d4d4' }}>{p.personalGoogleAuth.connectedUser.email}</strong>
                                        {p.personalGoogleAuth.connectedUser.name && (
                                            <span> ({p.personalGoogleAuth.connectedUser.name})</span>
                                        )}
                                        {p.personalGoogleAuth.connectedUser.connectedAt && (
                                            <span style={{ marginLeft: '8px', fontSize: '0.9em', opacity: 0.7 }}>
                                                · connected {new Date(p.personalGoogleAuth.connectedUser.connectedAt).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Disconnect Personal Google Confirmation Modal */}
                    {p.showPersonalDisconnectConfirm && (
                        <div className="modal-overlay">
                            <div className="modal-content">
                                <h3><i className="fas fa-exclamation-triangle" style={{color: '#f59e0b'}}></i> Disconnect Personal Google</h3>
                                <p>Are you sure you want to disconnect your personal Google account?</p>
                                <p style={{fontSize: '0.9em', color: '#9ca3af'}}>
                                    You will lose access to Google Drive, Sheets, and Docs for this account until you reconnect.
                                </p>
                                <div className="modal-actions">
                                    <button className="btn btn-secondary" onClick={() => p.setShowPersonalDisconnectConfirm(false)}>
                                        Cancel
                                    </button>
                                    <button className="btn btn-danger" onClick={p.confirmDisconnectPersonalGoogle} style={{backgroundColor:'#dc2626',color:'#fff',borderColor:'#dc2626'}}>
                                        Disconnect
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

{/* User LLM API Keys - Per Provider List */}
                    <div className="settings-category" id="user-api-settings">
                        <h3>
                            <i className="fas fa-key"></i> LLM API Keys
                            <span className="badge badge-user">USER</span>
                        </h3>
                        <div id="user-api-settings-content">
                            {p.userSettingsLoading ? (
                                <div className="loading">
                                    <i className="fas fa-spinner fa-spin"></i> Loading user settings...
                                </div>
                            ) : p.providers && p.providers.length > 0 ? (
                                <div className="user-provider-list">
                                    {p.providers.map((provider) => {
                                        const providerSlug = String(provider?.slug || provider?.id || '').toLowerCase();
                                        const providerId = String(provider?.id || provider?.slug || '');
                                        const providerName = provider?.name || `${providerSlug.charAt(0).toUpperCase()}${providerSlug.slice(1)}`;
                                        const apiTypeLabel = provider?.api_type ? p.getApiTypeLabel(provider.api_type) : providerName;
                                        const keyName = p.providerToKeyName(provider);
                                        const value = (p.userSettings.llm_keys && p.userSettings.llm_keys[keyName]) || '';
                                        const placeholder = p.getApiKeyPlaceholder(provider?.api_type);
                                        const maskedClass = value && p.isMasked(value) && value.length > 4 ? 'masked-key' : '';
                                        const validation = p.providerValidation[providerId] || p.providerValidation[providerSlug] || { loading: false, status: null, error: null };
                                        const isDirty = p.isProviderDirty(provider);
                                        const isPansynapse = providerSlug === 'pansynapse' || String(provider?.api_type || '').toLowerCase() === 'pansynapse';
                                        const pansynapseUserIdDirty = isPansynapse && p.isPansynapseUserIdDirty();
                                        return (
                                            <div className="user-provider-row" key={providerId || keyName}>
                                                <div className="user-provider-row-info">
                                                    <div className="setting-label">
                                                        {providerName}
                                                        {validation.status === 'valid' && (
                                                            <i className="fas fa-check-circle api-key-icon-valid" title="Connection successful"></i>
                                                        )}
                                                        {validation.status === 'invalid' && (
                                                            <i className="fas fa-times-circle api-key-icon-invalid" title={validation.error || 'Invalid key'}></i>
                                                        )}
                                                        {validation.status === 'error' && (
                                                            <i className="fas fa-exclamation-circle api-key-icon-error" title={validation.error || 'Connection error'}></i>
                                                        )}
                                                    </div>
                                                    <div className="setting-description">API Type: {apiTypeLabel}</div>
                                                </div>
                                                <div className="user-provider-row-input">
                                                    <div className="user-provider-row-key-field">
                                                        <input
                                                            type="text"
                                                            value={value}
                                                            onChange={(e) => p.handleLlmKeyChange(provider, e.target.value)}
                                                            placeholder={placeholder}
                                                            disabled={p.systemSettingsLoading || p.saving}
                                                            className={maskedClass}
                                                        />
                                                        {p.hasStoredKey(value) && (
                                                            <button
                                                                type="button"
                                                                className="btn-key-clear"
                                                                onClick={() => p.handleClearLlmKey(provider)}
                                                                disabled={p.saving}
                                                                title="Remove API key"
                                                                aria-label={`Remove ${providerName} API key`}
                                                            >
                                                                <i className="fas fa-times" aria-hidden="true"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={() => p.handleTestApiKey(provider)}
                                                        disabled={p.saving || !value || validation.loading}
                                                        title="Open model-based API key test"
                                                    >
                                                        {validation.loading ? (
                                                            <><i className="fas fa-spinner fa-spin"></i></>
                                                        ) : (
                                                            <><i className="fas fa-plug"></i> Test Model</>
                                                        )}
                                                    </button>
                                                </div>
                                                {isPansynapse && (
                                                    <div className="user-provider-row-input user-provider-row-input-secondary">
                                                        <div className="user-provider-row-info">
                                                            <div className="setting-label">Pansynapse User ID</div>
                                                            <div className="setting-description">
                                                                Your Pansynapse/CosmicMind user id. Sent to CosmicMind as user_id with your project id.
                                                            </div>
                                                        </div>
                                                        <div className="user-provider-row-key-field">
                                                            <input
                                                                type="text"
                                                                value={p.userSettings.pansynapseUserId || ''}
                                                                onChange={(e) => p.handlePansynapseUserIdChange(e.target.value)}
                                                                placeholder="Pansynapse user id"
                                                                disabled={p.systemSettingsLoading || p.saving}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {pansynapseUserIdDirty && !validation.loading && (
                                                    <div className="api-key-status api-key-pending">
                                                        <i className="fas fa-info-circle"></i> Pansynapse user ID unsaved
                                                    </div>
                                                )}
                                                {isDirty && !validation.loading && (
                                                    <div className="api-key-status api-key-pending">
                                                        <i className="fas fa-info-circle"></i> Unsaved
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="setting-item">
                                    <div>
                                        <div className="setting-label">LLM API Keys</div>
                                        <div className="setting-description">
                                            <span style={{ color: '#9ca3af' }}>
                                                No providers configured. Contact your system administrator to enable AI providers.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="settings-category" id="user-web-search-settings">
                        <h3>
                            <i className="fas fa-globe"></i> Web Search API Keys
                            <span className="badge badge-user">USER</span>
                        </h3>
                        <div id="user-web-search-settings-content">
                            {p.userSettingsLoading ? (
                                <div className="loading">
                                    <i className="fas fa-spinner fa-spin"></i> Loading user settings...
                                </div>
                            ) : (
                                <div className="user-provider-list">
                                    {WEB_SEARCH_PROVIDERS.map((provider) => {
                                        const value = (p.userSettings.web_search_keys || {})[provider.keyName] || '';
                                        const maskedClass = value && p.isMasked(value) && value.length > 4 ? 'masked-key' : '';
                                        const isDirty = p.isWebSearchKeyDirty(provider.keyName);
                                        const feelLuckyOn = provider.toggleKey && isFeelLuckyEnabled(value);
                                        return (
                                            <div className="user-provider-row" key={provider.keyName}>
                                                <div className="user-provider-row-info">
                                                    <div className="setting-label">{provider.name}</div>
                                                    <div className="setting-description">{provider.description}</div>
                                                </div>
                                                {provider.toggleKey ? (
                                                    <div className="user-provider-row-input user-provider-row-toggle">
                                                        <label className="feel-lucky-toggle">
                                                            <input
                                                                type="checkbox"
                                                                checked={feelLuckyOn}
                                                                onChange={(e) => p.handleWebSearchKeyChange(
                                                                    provider.keyName,
                                                                    e.target.checked ? 'y' : 'n'
                                                                )}
                                                                disabled={p.saving}
                                                            />
                                                            <span>Enabled (no API key required)</span>
                                                        </label>
                                                    </div>
                                                ) : (
                                                <div className="user-provider-row-input">
                                                    <div className="user-provider-row-key-field">
                                                        <input
                                                            type="password"
                                                            autoComplete="off"
                                                            value={value}
                                                            onChange={(e) => p.handleWebSearchKeyChange(provider.keyName, e.target.value)}
                                                            placeholder={`Paste ${provider.keyName}`}
                                                            disabled={p.saving}
                                                            className={maskedClass}
                                                        />
                                                        {p.hasStoredKey(value) && (
                                                            <button
                                                                type="button"
                                                                className="btn-key-clear"
                                                                onClick={() => p.handleClearWebSearchKey(provider.keyName)}
                                                                disabled={p.saving}
                                                                title="Remove API key"
                                                                aria-label={`Remove ${provider.name} API key`}
                                                            >
                                                                <i className="fas fa-times" aria-hidden="true"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                )}
                                                {isDirty && (
                                                    <div className="api-key-status api-key-pending">
                                                        <i className="fas fa-info-circle"></i> Unsaved — click Save User Settings
                                                    </div>
                                                )}
                                                {!isDirty && !provider.toggleKey && value && p.isMasked(value) && (
                                                    <div className="api-key-status api-key-saved">
                                                        <i className="fas fa-check-circle"></i> Key saved
                                                    </div>
                                                )}
                                                {!isDirty && provider.toggleKey && feelLuckyOn && (
                                                    <div className="api-key-status api-key-saved">
                                                        <i className="fas fa-check-circle"></i> Scrape search enabled
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="settings-category" id="user-mcp-settings">
                        <h3>
                            <i className="fas fa-plug"></i> MCP Servers
                            <span className="badge badge-user">USER</span>
                        </h3>
                        <SettingsMcpPanel
                            feelLuckyMcpServerId={p.userSettings.feelLuckyMcpServerId || ''}
                            onFeelLuckyMcpServerChange={p.handleFeelLuckyMcpServerChange}
                            feelLuckyMcpDirty={p.feelLuckyMcpDirty}
                            disabled={p.saving || p.userSettingsLoading}
                        />
                    </div>

                    {/* Save User Settings Button */}
                    <div className="settings-save-section">
                        <button
                            className="btn btn-primary btn-save-settings"
                            onClick={p.handleSaveUserSettings}
                            disabled={!p.hasTabChanges() || p.saving || p.userSettingsLoading || !p.user?.email}
                        >
                            {p.saving ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i> Saving...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-save"></i> Save User Settings
                                </>
                            )}
                        </button>
                        {p.hasTabChanges() && (
                            <div className="unsaved-changes-indicator">
                                <i className="fas fa-exclamation-circle"></i> You have unsaved changes
                            </div>
                        )}
                    </div>
                </div>
    );
}
